import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Calendar,
  Clock,
  Video,
  CheckCircle,
  XCircle,
  Star,
  RefreshCcw,
  AlertCircle,
  Film,
  MessageSquare,
  Download,
} from 'lucide-react'
import { sessionsApi } from '../../lib/api/sessions'
import { encodeId } from '../../lib/utils/idObfuscation'
import { parseVtt } from '../../lib/utils/parseVtt'
import { buildRecordingSrc } from '../../lib/utils/streamUrl'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import Modal from '../../components/ui/Modal'
import EmptyState from '../../components/ui/EmptyState'
import { useProfileTimezone } from '../../lib/hooks/useProfileTimezone'
import { formatDateInTz, formatTimeInTz } from '../../lib/utils/timezone'
import {
  UNATTENDED_REASON,
  attendanceHint,
  isUnattendedState,
  type AttendanceState,
} from '../../lib/utils/attendance'

// ── Types ─────────────────────────────────────────────────────────────────────

type Tab = 'upcoming' | 'completed' | 'cancelled' | 'no_show'

interface InterviewItem {
  source: 'booking' | 'session'
  source_id: number
  title: string
  interviewer_alias: string | null
  interviewer_id: number | null
  scheduled_at: string
  duration_minutes: number
  status: string
  zoom_join_url: string | null
  zoom_password: string | null
  cancellation_reason: string | null
  no_show_party: string | null
  attendance_state: AttendanceState
  created_at: string
  has_recording: boolean
  has_transcript: boolean
}

// Party-keyed no-show wording shown to the client.
function noShowLabel(party: string | null): string {
  if (party === 'client') return 'You missed this session'
  if (party === 'interviewer') return "Interviewer didn't join"
  return 'Session did not take place'
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(iso: string, tz: string) {
  return {
    day: parseInt(new Intl.DateTimeFormat('en-US', { timeZone: tz, day: 'numeric' }).format(new Date(iso)), 10),
    month: new Intl.DateTimeFormat('en-US', { timeZone: tz, month: 'short' }).format(new Date(iso)).toUpperCase(),
    time: formatTimeInTz(iso, tz),
    full: formatDateInTz(iso, tz),
  }
}

const STATUS_LABEL: Record<string, string> = {
  confirmed: 'Confirmed', scheduled: 'Scheduled', in_progress: 'In Progress',
  completed: 'Completed', cancelled: 'Cancelled', pending_payment: 'Awaiting Payment',
  payment_expired: 'Payment Expired', payment_failed: 'Payment Failed',
  no_show: 'No-show',
}
const STATUS_COLOR: Record<string, string> = {
  confirmed: '#16a34a', scheduled: '#2563eb', in_progress: '#7c3aed',
  completed: '#16a34a', cancelled: '#6b7280', pending_payment: '#d97706',
  payment_expired: '#dc2626', payment_failed: '#dc2626',
  no_show: '#dc2626',
}

const STAR_LABELS = ['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent']

function hoursUntil(iso: string) {
  return (new Date(iso).getTime() - Date.now()) / (1000 * 60 * 60)
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function ClientInterviews() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const timezone = useProfileTimezone()

  const [activeTab, setActiveTab] = useState<Tab>(() => {
    const src = searchParams.get('source')
    // If navigated from home with a source param, it's always upcoming
    return src ? 'upcoming' : 'upcoming'
  })
  const [highlightId, setHighlightId] = useState<string | null>(() => {
    const src = searchParams.get('source')
    const id = searchParams.get('id')
    return src && id ? `${src}-${id}` : null
  })
  const highlightRef = useRef<HTMLDivElement | null>(null)
  const [items, setItems] = useState<InterviewItem[]>([])
  const [loading, setLoading] = useState(true)
  const [counts, setCounts] = useState({ upcoming: 0, completed: 0, cancelled: 0, no_show: 0 })

  // Rated tracking: "session-{id}" | "booking-{id}"
  const [ratedKeys, setRatedKeys] = useState<Set<string>>(new Set())

  // Feedback tracking: "session-{id}" | "booking-{id}"
  const [feedbackKeys, setFeedbackKeys] = useState<Set<string>>(new Set())

  // Reschedule modal
  const [rescheduleTarget, setRescheduleTarget] = useState<InterviewItem | null>(null)
  const [newDate, setNewDate] = useState('')
  const [newTime, setNewTime] = useState('')
  const [rescheduling, setRescheduling] = useState(false)

  // Cancel modal
  const [cancelTarget, setCancelTarget] = useState<InterviewItem | null>(null)
  const [cancelling, setCancelling] = useState(false)

  // Recording modal
  const [recordingTarget, setRecordingTarget] = useState<InterviewItem | null>(null)
  const [recordingData, setRecordingData] = useState<{
    recording_url: string; transcript_url: string | null; duration_seconds: number | null
  } | null>(null)
  const [recordingLoading, setRecordingLoading] = useState(false)
  const [recordingError, setRecordingError] = useState('')
  const [transcriptCues, setTranscriptCues] = useState<{ time: string; speaker: string; text: string }[]>([])

  // Transcript-only modal
  const [transcriptTarget, setTranscriptTarget] = useState<InterviewItem | null>(null)
  const [transcriptModalCues, setTranscriptModalCues] = useState<{ time: string; speaker: string; text: string }[]>([])
  const [transcriptModalLoading, setTranscriptModalLoading] = useState(false)
  const [transcriptModalError, setTranscriptModalError] = useState('')

  // Rating modal
  const [rateTarget, setRateTarget] = useState<InterviewItem | null>(null)
  const [rating, setRating] = useState(0)
  const [hoverRating, setHoverRating] = useState(0)
  const [ratingComment, setRatingComment] = useState('')
  const [submittingRating, setSubmittingRating] = useState(false)
  const [ratingError, setRatingError] = useState('')

  // ── Data fetching ──────────────────────────────────────────────────────────

  const fetchTab = useCallback(async (tab: Tab) => {
    setLoading(true)
    try {
      const data = await sessionsApi.getClientInterviews(tab)
      const list: InterviewItem[] = Array.isArray(data) ? data : []
      setItems(list)
      setCounts((prev) => ({ ...prev, [tab]: list.length }))
    } catch {
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchTab(activeTab) }, [activeTab, fetchTab])

  // Scroll to and briefly highlight card when navigated from another page
  useEffect(() => {
    if (!highlightId || loading || items.length === 0) return
    const el = highlightRef.current
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      // Clear highlight after 3 seconds
      const t = setTimeout(() => setHighlightId(null), 3000)
      return () => clearTimeout(t)
    }
  }, [highlightId, loading, items])

  // Load which completed items have feedback delivered
  useEffect(() => {
    if (activeTab !== 'completed') return
    sessionsApi.getFeedback().then((data) => {
      const list = Array.isArray(data) ? data : data?.data ?? []
      const keys = new Set<string>()
      for (const fb of list) {
        if (fb.session_id) keys.add(`session-${fb.session_id}`)
        if (fb.booking_id) keys.add(`booking-${fb.booking_id}`)
      }
      setFeedbackKeys(keys)
    }).catch(() => {})
  }, [activeTab, items])

  // Check which completed items are already rated
  useEffect(() => {
    if (activeTab !== 'completed' || items.length === 0) return
    Promise.all(
      items.map(async (item) => {
        try {
          const rated =
            item.source === 'session'
              ? await sessionsApi.getRatingForSession(item.source_id)
              : await sessionsApi.getRatingForBooking(item.source_id)
          return rated ? `${item.source}-${item.source_id}` : null
        } catch {
          return null
        }
      }),
    ).then((results) => {
      setRatedKeys(new Set(results.filter(Boolean) as string[]))
    })
  }, [activeTab, items])

  // ── Reschedule ─────────────────────────────────────────────────────────────

  const handleReschedule = async () => {
    if (!rescheduleTarget || !newDate || !newTime) return
    setRescheduling(true)
    try {
      const isoDate = new Date(`${newDate}T${newTime}:00`).toISOString()
      if (rescheduleTarget.source === 'booking') {
        await sessionsApi.rescheduleBooking(rescheduleTarget.source_id, isoDate)
      } else {
        await sessionsApi.rescheduleSession(rescheduleTarget.source_id, isoDate)
      }
      setRescheduleTarget(null)
      fetchTab(activeTab)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to reschedule')
    } finally {
      setRescheduling(false)
    }
  }

  // ── Cancel ─────────────────────────────────────────────────────────────────

  const handleCancel = async () => {
    if (!cancelTarget) return
    setCancelling(true)
    try {
      if (cancelTarget.source === 'booking') {
        await sessionsApi.cancelBooking(cancelTarget.source_id, 'Cancelled by client')
      } else {
        await sessionsApi.cancelSession(cancelTarget.source_id, 'Cancelled by client')
      }
      setCancelTarget(null)
      fetchTab(activeTab)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to cancel')
    } finally {
      setCancelling(false)
    }
  }

  // ── Rating ─────────────────────────────────────────────────────────────────

  const openRating = (item: InterviewItem) => {
    setRateTarget(item)
    setRating(0)
    setHoverRating(0)
    setRatingComment('')
    setRatingError('')
  }

  const handleSubmitRating = async () => {
    if (!rateTarget || rating === 0) return
    setSubmittingRating(true)
    setRatingError('')
    try {
      await sessionsApi.submitRating(
        rateTarget.source,
        rateTarget.source_id,
        rating,
        ratingComment || undefined,
      )
      setRatedKeys((prev) => new Set([...prev, `${rateTarget.source}-${rateTarget.source_id}`]))
      setRateTarget(null)
    } catch (err) {
      setRatingError(err instanceof Error ? err.message : 'Failed to submit rating')
    } finally {
      setSubmittingRating(false)
    }
  }

  // ── Recording ──────────────────────────────────────────────────────────────

  const openRecording = async (item: InterviewItem) => {
    setRecordingTarget(item)
    setRecordingData(null)
    setRecordingError('')
    setTranscriptCues([])
    setRecordingLoading(true)
    try {
      const res =
        item.source === 'booking'
          ? await sessionsApi.getBookingRecording(item.source_id)
          : await sessionsApi.getRecording(item.source_id)
      const data = res.data ?? res
      setRecordingData(data)
      if (data.transcript_url) {
        try {
          const vttRes = await fetch(data.transcript_url)
          const vttText = await vttRes.text()
          setTranscriptCues(parseVtt(vttText))
        } catch {
          // transcript fetch failed silently — link fallback still shown
        }
      }
    } catch {
      setRecordingError('Recording is not available yet. Please check back after the session ends.')
    } finally {
      setRecordingLoading(false)
    }
  }

  const openTranscriptModal = async (item: InterviewItem) => {
    setTranscriptTarget(item)
    setTranscriptModalCues([])
    setTranscriptModalError('')
    setTranscriptModalLoading(true)
    try {
      const vttText =
        item.source === 'booking'
          ? await sessionsApi.getBookingTranscript(item.source_id)
          : await sessionsApi.getSessionTranscript(item.source_id)
      const cues = parseVtt(vttText)
      if (cues.length === 0) setTranscriptModalError('No transcript content available.')
      else setTranscriptModalCues(cues)
    } catch {
      setTranscriptModalError('Transcript is not available for this session.')
    } finally {
      setTranscriptModalLoading(false)
    }
  }

  // ── Card renderer ──────────────────────────────────────────────────────────

  const renderCard = (item: InterviewItem) => {
    const { day, month, time } = fmtDate(item.scheduled_at, timezone)
    const isUpcoming = activeTab === 'upcoming'
    const isCancelled = activeTab === 'cancelled'
    const isNoShow = activeTab === 'no_show'
    // The server partitions Completed / No-show / Cancelled by ATTENDANCE, not
    // by the raw status: a past booking still marked 'confirmed' arrives on the
    // No-show tab (one party joined) or the Cancelled tab (nobody joined). So
    // the pill has to read the tab, not item.status, or those rows would say
    // "Confirmed".
    const isCompleted = activeTab === 'completed'
    // Cancelled because nobody joined, as opposed to somebody cancelling it.
    const isUnattended = isCancelled && isUnattendedState(item.attendance_state)
    const color =
      isNoShow || isCancelled ? '#dc2626' : STATUS_COLOR[item.status] || '#6b7280'
    const label = isNoShow
      ? 'No-show'
      : isCancelled
        ? 'Cancelled'
        : STATUS_LABEL[item.status] || item.status
    const ratedKey = `${item.source}-${item.source_id}`
    const isRated = ratedKeys.has(ratedKey)
    const hasFeedback = feedbackKeys.has(ratedKey)
    const startMs = new Date(item.scheduled_at).getTime()
    const msUntilMeeting = startMs - Date.now()
    const meetingEndMs = startMs + (item.duration_minutes ?? 60) * 60 * 1000
    // Enabled from 45 min before start until the meeting ends — not indefinitely afterwards.
    const canJoin = !!item.zoom_join_url && msUntilMeeting <= 45 * 60 * 1000 && Date.now() < meetingEndMs
    const canModify = hoursUntil(item.scheduled_at) > 24

    const isHighlighted = highlightId === ratedKey

    return (
      <div
        key={ratedKey}
        ref={isHighlighted ? highlightRef : undefined}
        className={`ci-card${isHighlighted ? ' ci-card--highlight' : ''}`}
      >
        {/* Date badge + status */}
        <div className="ci-date-col">
          <div className="ci-date-badge">
            <span className="ci-day">{day}</span>
            <span className="ci-month">{month}</span>
          </div>
          {(isUpcoming || isCancelled || isNoShow) && (
            <span
              className="ci-status-pill"
              style={{ background: `${color}18`, color }}
              title={attendanceHint(item.attendance_state, item.no_show_party)}
            >
              {label}
            </span>
          )}
        </div>

        {/* Details */}
        <div className="ci-details">
          <h3 className="ci-title">{item.title}</h3>
          <div className="ci-meta">
            <span><Clock size={13} />{time}</span>
            <span>{item.duration_minutes} min</span>
          </div>

          {item.interviewer_alias && (
            <div className="ci-interviewer">
              <div className="ci-avatar">{item.interviewer_alias.charAt(0).toUpperCase()}</div>
              <div>
                <p className="ci-iv-name">{item.interviewer_alias}</p>
                <p className="ci-iv-role">Interview Coach</p>
              </div>
            </div>
          )}

          {isCancelled && item.cancellation_reason && (
            <div style={{ fontSize: 12, color: '#dc2626', marginTop: 6, lineHeight: 1.5 }}>
              Reason: {item.cancellation_reason}
            </div>
          )}

          {isNoShow && (
            <div className="ci-cancel-reason" style={{ marginTop: 6 }}>
              <AlertCircle size={13} />
              <span>{noShowLabel(item.no_show_party)}</span>
            </div>
          )}

          {isUnattended && (
            <div className="ci-cancel-reason" style={{ marginTop: 6 }}>
              <AlertCircle size={13} />
              <span>{UNATTENDED_REASON}</span>
            </div>
          )}

          {isUpcoming && item.zoom_join_url && (
            <div className="ci-zoom-badge">
              <Video size={13} style={{ color: '#16a34a', flexShrink: 0 }} />
              <span style={{ color: '#16a34a', fontWeight: 500 }}>Zoom Ready</span>
              {item.zoom_password && (
                <span className="ci-zoom-pwd">
                  Password: <strong>{item.zoom_password}</strong>
                </span>
              )}
            </div>
          )}

          {item.cancellation_reason && (
            <div className="ci-cancel-reason">
              <AlertCircle size={13} />
              <span>{item.cancellation_reason}</span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="ci-actions">
          {isUpcoming && (
            <>
              {canJoin ? (
                <button
                  className="btn btn-primary"
                  onClick={() => navigate(`/client/interviews/${item.source}/${item.source === 'booking' ? encodeId(item.source_id) : item.source_id}`)}
                >
                  <Video size={15} /> Join
                </button>
              ) : (
                <button className="btn btn-primary" disabled style={{ opacity: 0.4 }}>
                  <Video size={15} /> Join
                </button>
              )}
              {item.source === 'booking' && (
                <button
                  className="btn btn-outline"
                  onClick={() => navigate(`/client/schedule?rescheduleId=${item.source_id}`)}
                  disabled={!canModify}
                  title={!canModify ? 'Reschedules must be made at least 24 hours in advance' : undefined}
                >
                  <RefreshCcw size={14} /> Reschedule
                </button>
              )}
              <button
                className="btn btn-outline ci-cancel-btn"
                onClick={() => setCancelTarget(item)}
                disabled={!canModify}
                title={!canModify ? 'Cancellations must be made at least 24 hours in advance' : undefined}
              >
                <XCircle size={14} /> Cancel
              </button>
              {!canModify && item.status === 'confirmed' && (
                <div className="ci-policy-note">
                  <AlertCircle size={12} />
                  <span>Changes not allowed within 24 hours of the session</span>
                </div>
              )}
            </>
          )}

          {isCompleted && (
            <>
              <button
                className="btn btn-outline"
                onClick={() => openRecording(item)}
                disabled={!item.has_recording}
                title={!item.has_recording ? 'Recording not yet available' : undefined}
              >
                <Film size={14} /> View Recording
              </button>
              <button
                className="btn btn-outline ci-transcript-btn"
                onClick={() => openTranscriptModal(item)}
                disabled={!item.has_transcript}
                title={!item.has_transcript ? 'Transcript not yet available' : undefined}
              >
                <MessageSquare size={14} /> View Transcript
              </button>

              {isRated ? (
                <span className="ci-rated-badge">
                  <CheckCircle size={13} /> Rated
                </span>
              ) : (
                <button className="btn btn-outline ci-rate-btn" onClick={() => openRating(item)}>
                  <Star size={14} /> Rate Interviewer
                </button>
              )}

              <button
                className="btn btn-outline ci-feedback-btn"
                onClick={() => navigate(`/client/feedback?source=${item.source}&id=${item.source_id}`)}
                disabled={!hasFeedback}
                title={!hasFeedback ? 'Feedback not yet available' : undefined}
              >
                <MessageSquare size={14} /> View Feedback
              </button>
            </>
          )}
        </div>
      </div>
    )
  }

  // ── JSX ────────────────────────────────────────────────────────────────────

  return (
    <div className="client-interviews">
      <div className="page-header">
        <h1 className="page-title">My Interviews</h1>
        <p className="page-subtitle">View and manage your interview sessions</p>
      </div>

      <div className="ci-policy-banner">
        <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
        <span>
          <strong>Cancellation & Reschedule Policy:</strong> You can cancel or reschedule
          your session up to <strong>24 hours before</strong> the scheduled start time.
          Requests made within 24 hours are not eligible for a refund.
        </span>
      </div>

      <div className="tabs">
        <button
          type="button"
          className={`tab ${activeTab === 'upcoming' ? 'active' : ''}`}
          onClick={() => setActiveTab('upcoming')}
        >
          <Calendar size={15} />
          Sessions (Upcoming)
          {counts.upcoming > 0 && <span className="tab-count">{counts.upcoming}</span>}
        </button>
        <button
          type="button"
          className={`tab ${activeTab === 'completed' ? 'active' : ''}`}
          onClick={() => setActiveTab('completed')}
        >
          <CheckCircle size={15} />
          Sessions (Completed)
          {counts.completed > 0 && <span className="tab-count">{counts.completed}</span>}
        </button>
        <button
          type="button"
          className={`tab ${activeTab === 'cancelled' ? 'active' : ''}`}
          onClick={() => setActiveTab('cancelled')}
        >
          <XCircle size={15} />
          Sessions (Cancelled)
          {counts.cancelled > 0 && <span className="tab-count">{counts.cancelled}</span>}
        </button>
        <button
          type="button"
          className={`tab ${activeTab === 'no_show' ? 'active' : ''}`}
          onClick={() => setActiveTab('no_show')}
        >
          <AlertCircle size={15} />
          Sessions (No-show)
          {counts.no_show > 0 && <span className="tab-count">{counts.no_show}</span>}
        </button>
      </div>

      {loading ? (
        <LoadingSpinner message="Loading interviews..." />
      ) : items.length === 0 ? (
        <EmptyState
          icon={Calendar}
          title={
            activeTab === 'upcoming'
              ? 'No upcoming interviews'
              : activeTab === 'cancelled'
                ? 'No cancelled interviews'
                : activeTab === 'no_show'
                  ? 'No missed interviews'
                  : 'No completed interviews'
          }
          message={
            activeTab === 'upcoming'
              ? 'Book a mock interview to see it here'
              : activeTab === 'cancelled'
                ? 'Sessions cancelled by you or an admin will appear here'
                : activeTab === 'no_show'
                  ? 'Sessions where a party did not join will appear here'
                  : 'Completed interviews will appear here'
          }
          actionLabel="Schedule a Session"
          onAction={() => navigate('/client/schedule')}
        />
      ) : (
        <div className="ci-list">{items.map(renderCard)}</div>
      )}

      {/* ── Reschedule Modal ──────────────────────────────────────────────── */}
      <Modal
        open={!!rescheduleTarget}
        onClose={() => setRescheduleTarget(null)}
        title="Reschedule Interview"
        footer={
          <>
            <button className="btn btn-outline" onClick={() => setRescheduleTarget(null)}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleReschedule}
              disabled={rescheduling || !newDate || !newTime}
            >
              {rescheduling ? 'Rescheduling...' : 'Confirm Reschedule'}
            </button>
          </>
        }
      >
        <div className="ci-refund-policy" style={{ marginBottom: 16 }}>
          <AlertCircle size={14} style={{ flexShrink: 0 }} />
          <span>
            Reschedules must be made at least 24 hours before your scheduled appointment
            time to qualify for a refund.
          </span>
        </div>
        <p style={{ color: 'var(--text-muted)', marginBottom: 20 }}>
          Select a new date and time for your session.
        </p>
        {rescheduleTarget && (
          <div className="ci-current-time">
            <span style={{ fontWeight: 600, color: 'var(--text-muted)' }}>Current:</span>
            <span>
              {fmtDate(rescheduleTarget.scheduled_at, timezone).full} at{' '}
              {fmtDate(rescheduleTarget.scheduled_at, timezone).time}
            </span>
          </div>
        )}
        <div className="form-group">
          <label className="form-label">New Date</label>
          <input
            type="date" className="form-input" value={newDate}
            min={new Date().toISOString().split('T')[0]}
            onChange={(e) => setNewDate(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label className="form-label">New Time</label>
          <input type="time" className="form-input" value={newTime}
            onChange={(e) => setNewTime(e.target.value)} />
        </div>
      </Modal>

      {/* ── Cancel Modal ──────────────────────────────────────────────────── */}
      <Modal
        open={!!cancelTarget}
        onClose={() => setCancelTarget(null)}
        title="Cancel Interview"
        maxWidth={440}
        footer={
          <>
            <button className="btn btn-outline" onClick={() => setCancelTarget(null)}>
              Keep Interview
            </button>
            <button className="btn btn-danger" onClick={handleCancel} disabled={cancelling}>
              {cancelling ? 'Cancelling...' : 'Yes, Cancel'}
            </button>
          </>
        }
      >
        <div style={{ textAlign: 'center', padding: '16px 0' }}>
          <XCircle size={48} style={{ color: '#dc2626', marginBottom: 16 }} />
          <p style={{ fontWeight: 600, fontSize: 16, marginBottom: 8 }}>
            Are you sure you want to cancel this interview?
          </p>
          <p style={{ fontSize: 14, color: 'var(--text-dark)', fontWeight: 500 }}>
            {cancelTarget?.title}
          </p>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 10 }}>
            This action cannot be undone. Your slot will be released.
          </p>
        </div>
        <div className="ci-refund-policy">
          <AlertCircle size={14} style={{ flexShrink: 0 }} />
          <span>
            Cancellations must be made at least 24 hours before your scheduled appointment
            time to qualify for a refund.
          </span>
        </div>
      </Modal>

      {/* ── Rate Interviewer Modal ────────────────────────────────────────── */}
      <Modal
        open={!!rateTarget}
        onClose={() => setRateTarget(null)}
        title="Rate Your Interviewer"
        maxWidth={480}
        footer={
          <>
            <button className="btn btn-outline" onClick={() => setRateTarget(null)}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSubmitRating}
              disabled={submittingRating || rating === 0}
            >
              {submittingRating ? 'Submitting...' : 'Submit Rating'}
            </button>
          </>
        }
      >
        {rateTarget?.interviewer_alias && (
          <div className="ci-rate-header">
            <div className="ci-rate-avatar">
              {rateTarget.interviewer_alias.charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="ci-rate-name">{rateTarget.interviewer_alias}</p>
              <p className="ci-rate-session">{rateTarget.title}</p>
            </div>
          </div>
        )}

        <p style={{ color: 'var(--text-muted)', marginBottom: 4, fontSize: 14 }}>
          How was your overall experience?
        </p>

        <div className="ci-stars">
          {[1, 2, 3, 4, 5].map((s) => (
            <button
              key={s}
              type="button"
              className="ci-star-btn"
              onClick={() => setRating(s)}
              onMouseEnter={() => setHoverRating(s)}
              onMouseLeave={() => setHoverRating(0)}
            >
              <Star
                size={36}
                fill={s <= (hoverRating || rating) ? '#d4af37' : 'none'}
                stroke={s <= (hoverRating || rating) ? '#d4af37' : '#cbd5e1'}
              />
            </button>
          ))}
        </div>
        <div className="ci-star-labels">
          {['Poor', 'Fair', 'Good', 'Very Good', 'Excellent'].map((l) => (
            <span key={l}>{l}</span>
          ))}
        </div>
        {rating > 0 && (
          <div className="ci-star-selected">
            {STAR_LABELS[rating]} — {rating} / 5 stars
          </div>
        )}

        <div className="form-group" style={{ marginTop: 16 }}>
          <label className="form-label">
            Additional Comments{' '}
            <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(Optional)</span>
          </label>
          <textarea
            className="form-input"
            rows={3}
            placeholder="Share your experience — what went well, what could be improved..."
            value={ratingComment}
            onChange={(e) => setRatingComment(e.target.value)}
            style={{ resize: 'vertical', fontFamily: 'inherit' }}
          />
        </div>

        {ratingError && (
          <div style={{
            padding: '10px 14px', background: 'rgba(220,38,38,0.06)',
            border: '1px solid rgba(220,38,38,0.2)', borderRadius: 8,
            color: '#dc2626', fontSize: 13, marginTop: 8,
          }}>
            {ratingError}
          </div>
        )}
      </Modal>

      {/* ── Recording Modal ───────────────────────────────────────────────── */}
      <Modal
        open={!!recordingTarget}
        onClose={() => { setRecordingTarget(null); setRecordingData(null); setRecordingError(''); setTranscriptCues([]) }}
        title="Session Recording"
        maxWidth={720}
      >
        {recordingLoading && (
          <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)' }}>
            Loading recording…
          </div>
        )}

        {!recordingLoading && recordingError && (
          <div className="ci-rec-unavailable">
            <Film size={40} style={{ color: 'var(--text-muted)', marginBottom: 12 }} />
            <p style={{ fontWeight: 600, marginBottom: 6 }}>Recording Not Available</p>
            <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>{recordingError}</p>
          </div>
        )}

        {!recordingLoading && recordingData && (
          <div className="ci-rec-body">
            <video
              className="ci-rec-video"
              src={buildRecordingSrc(recordingData.recording_url) ?? undefined}
              controls
              controlsList="nodownload"
              onLoadedMetadata={(e) => {
                const d = Math.round((e.target as HTMLVideoElement).duration)
                if (d && isFinite(d)) {
                  setRecordingData((prev) => prev ? { ...prev, duration_seconds: d } : prev)
                }
              }}
            />
            {recordingData.duration_seconds != null && (
              <p className="ci-rec-duration">
                <Clock size={13} />
                Duration: {Math.floor(recordingData.duration_seconds / 60)}m {recordingData.duration_seconds % 60}s
              </p>
            )}
            {transcriptCues.length > 0 && (
              <div className="ci-transcript">
                <div className="ci-transcript-header">
                  <MessageSquare size={15} />
                  <span>Transcript Excerpts</span>
                </div>
                <div className="ci-transcript-list">
                  {transcriptCues.map((cue, i) => (
                    <div key={i} className="ci-transcript-cue">
                      <span className="ci-cue-time">{cue.time}</span>
                      <div className="ci-cue-body">
                        {cue.speaker && (
                          <span className={`ci-cue-speaker ${cue.speaker.toLowerCase() === 'interviewer' ? 'ci-speaker-iv' : 'ci-speaker-cl'}`}>
                            {cue.speaker}
                          </span>
                        )}
                        <p className="ci-cue-text">{cue.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {transcriptCues.length === 0 && recordingData.transcript_url && recordingTarget && (
              <button
                className="btn btn-outline ci-rec-transcript-btn"
                onClick={async () => {
                  try {
                    const text = recordingTarget.source === 'booking'
                      ? await sessionsApi.getBookingTranscript(recordingTarget.source_id)
                      : await sessionsApi.getSessionTranscript(recordingTarget.source_id)
                    const cues = parseVtt(text)
                    const formatted = cues.length > 0
                      ? cues.map((c) => c.speaker ? `[${c.time}] ${c.speaker}: ${c.text}` : `[${c.time}] ${c.text}`).join('\n\n')
                      : text
                    const blob = new Blob([formatted], { type: 'text/plain' })
                    const url = URL.createObjectURL(blob)
                    const a = document.createElement('a')
                    a.href = url
                    a.download = 'transcript.txt'
                    a.click()
                    URL.revokeObjectURL(url)
                  } catch {
                    // silently ignore
                  }
                }}
              >
                <Download size={14} /> Download Transcript
              </button>
            )}
          </div>
        )}
      </Modal>

      {/* ── Transcript Modal ───────────────────────────────────────────────── */}
      <Modal
        open={!!transcriptTarget}
        onClose={() => { setTranscriptTarget(null); setTranscriptModalCues([]); setTranscriptModalError('') }}
        title="Session Transcript"
        maxWidth={680}
        footer={
          <button
            className="btn btn-outline"
            onClick={() => { setTranscriptTarget(null); setTranscriptModalCues([]); setTranscriptModalError('') }}
          >
            Close
          </button>
        }
      >
        {transcriptModalLoading && (
          <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)' }}>
            Loading transcript…
          </div>
        )}
        {!transcriptModalLoading && transcriptModalError && (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)' }}>
            <MessageSquare size={36} style={{ marginBottom: 12, opacity: 0.4 }} />
            <p style={{ fontSize: 14 }}>{transcriptModalError}</p>
          </div>
        )}
        {!transcriptModalLoading && transcriptModalCues.length > 0 && (
          <div className="ci-tmodal-list">
            {transcriptModalCues.map((cue, i) => (
              <div key={i} className="ci-tmodal-cue">
                <span className="ci-tmodal-time">{cue.time}</span>
                <div className="ci-tmodal-body">
                  {cue.speaker && (
                    <span className={`ci-cue-speaker ${cue.speaker.toLowerCase() === 'interviewer' ? 'ci-speaker-iv' : 'ci-speaker-cl'}`}>
                      {cue.speaker}
                    </span>
                  )}
                  <p className="ci-cue-text">{cue.text}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal>

      <style>{styles}</style>
    </div>
  )
}

const styles = `
  .ci-list { display: flex; flex-direction: column; gap: 16px; }

  .ci-policy-banner {
    display: flex; align-items: flex-start; gap: 10px;
    padding: 12px 16px; margin-bottom: 20px;
    background: rgba(217,119,6,0.07); border: 1px solid rgba(217,119,6,0.3);
    border-radius: 10px; font-size: 13.5px; color: #78350f; line-height: 1.55;
  }

  .ci-card {
    display: flex; gap: 24px; align-items: flex-start;
    background: white; border-radius: 16px; padding: 24px;
    box-shadow: var(--shadow); border: 1px solid var(--border);
    transition: border-color 0.3s, box-shadow 0.3s;
  }
  .ci-card--highlight {
    border-color: var(--accent-gold, #d4af37);
    box-shadow: 0 0 0 3px rgba(212,175,55,0.25), var(--shadow);
    animation: ci-highlight-fade 3s ease forwards;
  }
  @keyframes ci-highlight-fade {
    0%   { border-color: var(--accent-gold, #d4af37); box-shadow: 0 0 0 3px rgba(212,175,55,0.35), var(--shadow); }
    70%  { border-color: var(--accent-gold, #d4af37); box-shadow: 0 0 0 3px rgba(212,175,55,0.25), var(--shadow); }
    100% { border-color: var(--border); box-shadow: var(--shadow); }
  }

  .ci-date-col {
    display: flex; flex-direction: column; align-items: center; gap: 8px; flex-shrink: 0;
  }
  .ci-date-badge {
    width: 64px; height: 64px; background: var(--primary-navy);
    color: var(--accent-cream, #f5f0dc); border-radius: 12px;
    display: flex; flex-direction: column; align-items: center; justify-content: center;
  }
  .ci-day  { font-size: 24px; font-weight: 700; line-height: 1; }
  .ci-month{ font-size: 11px; font-weight: 500; }

  .ci-status-pill {
    font-size: 11px; font-weight: 600; padding: 3px 8px;
    border-radius: 4px; white-space: nowrap;
  }

  .ci-details { flex: 1; min-width: 0; }
  .ci-title { font-size: 17px; font-weight: 600; margin-bottom: 6px; }

  .ci-meta {
    display: flex; gap: 16px; color: var(--text-muted);
    font-size: 13px; margin-bottom: 12px;
  }
  .ci-meta span { display: flex; align-items: center; gap: 5px; }

  .ci-interviewer {
    display: flex; align-items: center; gap: 10px;
    padding: 10px 14px; background: var(--accent-light,#f8f9fa);
    border-radius: 10px; width: fit-content; margin-bottom: 10px;
  }
  .ci-avatar {
    width: 36px; height: 36px; border-radius: 50%; background: var(--primary-navy);
    color: white; display: flex; align-items: center; justify-content: center;
    font-size: 14px; font-weight: 600; flex-shrink: 0;
  }
  .ci-iv-name { font-size: 13px; font-weight: 500; }
  .ci-iv-role { font-size: 11px; color: var(--text-muted); }

  .ci-zoom-badge {
    display: flex; align-items: center; gap: 8px;
    padding: 8px 12px; background: rgba(34,197,94,0.06);
    border: 1px solid rgba(34,197,94,0.2); border-radius: 8px;
    font-size: 13px; margin-top: 4px; width: fit-content;
  }
  .ci-zoom-pwd { color: var(--text-muted); margin-left: 6px; }

  .ci-cancel-reason {
    display: flex; align-items: flex-start; gap: 6px; font-size: 12px;
    color: #dc2626; margin-top: 6px; padding: 6px 10px;
    background: rgba(220,38,38,0.05); border-radius: 6px; width: fit-content;
  }

  .ci-actions {
    display: flex; flex-direction: column; gap: 8px;
    align-items: flex-end; justify-content: center; flex-shrink: 0;
  }

  .ci-cancel-btn   { border-color: #dc2626 !important; color: #dc2626 !important; }
  .ci-cancel-btn:hover { background: #dc2626 !important; color: white !important; }
  .ci-cancel-btn:disabled { border-color: #fca5a5 !important; color: #fca5a5 !important; cursor: not-allowed; }

  .ci-policy-note {
    display: flex; align-items: flex-start; gap: 5px;
    font-size: 11px; color: #92400e;
    background: rgba(217,119,6,0.08); border: 1px solid rgba(217,119,6,0.2);
    border-radius: 6px; padding: 6px 10px; max-width: 180px; text-align: left;
  }

  .ci-refund-policy {
    display: flex; align-items: flex-start; gap: 8px;
    padding: 10px 14px; background: rgba(217,119,6,0.06);
    border: 1px solid rgba(217,119,6,0.25); border-radius: 8px;
    font-size: 12px; color: #92400e; line-height: 1.5;
    margin-top: 12px;
  }

  .ci-rate-btn   { border-color: #d4af37 !important; color: #b8960c !important; }
  .ci-rate-btn:hover { background: #d4af37 !important; color: white !important; }

  .ci-feedback-btn { border-color: #7c3aed !important; color: #7c3aed !important; }
  .ci-feedback-btn:hover { background: #7c3aed !important; color: white !important; }

  .ci-rated-badge {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 8px 14px; background: rgba(212,175,55,0.1);
    color: #b8960c; border-radius: 8px; font-size: 13px; font-weight: 500;
  }

  .tab-count {
    display: inline-flex; align-items: center; justify-content: center;
    min-width: 20px; height: 20px; padding: 0 6px;
    background: var(--primary-navy); color: white;
    border-radius: 10px; font-size: 11px; font-weight: 700; margin-left: 4px;
  }
  .tab.active .tab-count { background: white; color: var(--primary-navy); }

  .ci-current-time {
    display: flex; align-items: center; gap: 8px;
    padding: 10px 14px; background: var(--accent-light,#f8f9fa);
    border-radius: 8px; margin-bottom: 16px; font-size: 13px;
  }

  .ci-rate-header {
    display: flex; align-items: center; gap: 14px;
    padding: 14px 16px; background: var(--accent-light,#f8f9fa);
    border-radius: 10px; margin-bottom: 20px;
  }
  .ci-rate-avatar {
    width: 48px; height: 48px; border-radius: 50%; background: var(--primary-navy);
    color: white; display: flex; align-items: center; justify-content: center;
    font-size: 20px; font-weight: 700; flex-shrink: 0;
  }
  .ci-rate-name    { font-size: 16px; font-weight: 600; }
  .ci-rate-session { font-size: 12px; color: var(--text-muted); margin-top: 2px; }

  .ci-stars {
    display: flex; justify-content: center; gap: 4px; margin: 16px 0 4px;
  }
  .ci-star-btn {
    background: none; border: none; padding: 4px; cursor: pointer; transition: transform 0.15s;
  }
  .ci-star-btn:hover { transform: scale(1.2); }

  .ci-star-labels {
    display: flex; justify-content: space-between;
    font-size: 11px; color: var(--text-muted); padding: 0 4px; margin-bottom: 4px;
  }
  .ci-star-selected {
    text-align: center; font-size: 13px; font-weight: 600; color: #b8960c;
    padding: 6px; background: rgba(212,175,55,0.08); border-radius: 6px;
  }

  .ci-rec-unavailable {
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    padding: 40px 20px; text-align: center;
  }

  .ci-rec-body { display: flex; flex-direction: column; gap: 14px; }

  .ci-rec-video {
    width: 100%; border-radius: 10px; background: #000;
    max-height: 400px; outline: none;
  }

  .ci-rec-duration {
    display: flex; align-items: center; gap: 6px;
    font-size: 13px; color: var(--text-muted);
  }

  .ci-rec-transcript-btn { align-self: flex-start; }

  .ci-transcript-btn { border-color: #7c3aed !important; color: #7c3aed !important; }
  .ci-transcript-btn:hover { background: #7c3aed !important; color: white !important; }

  .ci-tmodal-list {
    display: flex; flex-direction: column;
    border: 1px solid var(--border); border-radius: 10px;
  }
  .ci-tmodal-cue {
    display: flex; gap: 14px; padding: 12px 16px;
    border-bottom: 1px solid var(--border);
  }
  .ci-tmodal-cue:last-child { border-bottom: none; }
  .ci-tmodal-time {
    font-size: 12px; font-weight: 600; color: #7c3aed;
    white-space: nowrap; padding-top: 2px; min-width: 42px;
  }
  .ci-tmodal-body { display: flex; flex-direction: column; gap: 3px; flex: 1; }

  .ci-transcript {
    border: 1px solid var(--border); border-radius: 12px; overflow: hidden;
  }
  .ci-transcript-header {
    display: flex; align-items: center; gap: 8px;
    padding: 12px 16px; background: var(--accent-light, #f8f9fa);
    font-size: 13px; font-weight: 600; color: var(--text-primary);
    border-bottom: 1px solid var(--border);
  }
  .ci-transcript-list {
    display: flex; flex-direction: column;
    max-height: 320px; overflow-y: auto;
  }
  .ci-transcript-cue {
    display: flex; gap: 14px; padding: 12px 16px;
    border-bottom: 1px solid var(--border);
  }
  .ci-transcript-cue:last-child { border-bottom: none; }
  .ci-cue-time {
    font-size: 12px; font-weight: 600; color: var(--text-muted);
    white-space: nowrap; padding-top: 2px; min-width: 42px;
  }
  .ci-cue-body { display: flex; flex-direction: column; gap: 3px; flex: 1; }
  .ci-cue-speaker {
    font-size: 11px; font-weight: 700; text-transform: uppercase;
    letter-spacing: 0.5px; padding: 2px 7px; border-radius: 4px;
    width: fit-content;
  }
  .ci-speaker-iv { background: rgba(37,99,235,0.1); color: #2563eb; }
  .ci-speaker-cl { background: rgba(22,163,74,0.1); color: #16a34a; }
  .ci-cue-text { font-size: 13px; color: var(--text-primary); margin: 0; line-height: 1.5; }

  .btn-danger { background: #dc2626; border-color: #dc2626; color: white; }
  .btn-danger:hover { background: #b91c1c; }
  .btn-danger:disabled { background: #fca5a5; border-color: #fca5a5; }
  .btn:disabled { opacity: 0.45; cursor: not-allowed; }

  @media (max-width: 768px) {
    .ci-card { flex-direction: column; }
    .ci-date-col { flex-direction: row; }
    .ci-actions { flex-direction: row; flex-wrap: wrap; justify-content: flex-start; }
    .ci-rec-video { max-height: 220px; }
  }

  @media (max-width: 480px) {
    .ci-actions { flex-direction: column; }
    .ci-actions .btn { width: 100%; justify-content: center; }
    .ci-tmodal-cue { flex-direction: column; gap: 4px; }
    .ci-tmodal-time { min-width: unset; }
  }

  @media (max-width: 360px) {
    .ci-card { padding: 14px; }
  }
`
